import React, { Fragment, useMemo } from 'react';
import { SpeakerWithDetails } from '@/player-config/src/types/speaker-types';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { WordType } from '@/player-config/src/types/transcript-types';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getClipBroadcastId } from '@/player-config/src/utils/clipUtils';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { selectTranscript } from '@/player-config/src/selectors/contentMetadataStoreSelectors';

const DEAD_SPACE_MIN_DURATION = 1.5;
const DEAD_SPACE_VISIBILITY_CLASS = '@[10px]:block';

export default function TimelineSpeaker({
  speaker,
  speakerCount,
  compositeSegmentDragStart,
  compositeSegmentDragEnd,
  compositeSegment,
  fps
}: {
  speaker: SpeakerWithDetails;
  speakerCount: number;
  compositeSegmentDragStart: number;
  compositeSegmentDragEnd: number;
  compositeSegment: CompositeSegment;
  fps: number;
}) {
  const { clipData } = useClipsContext();
  const broadcastId = getClipBroadcastId(clipData) ?? '';
  const transcript = useContentMetadataStore(selectTranscript(broadcastId));

  const compositeStartOffsetTime = compositeSegment.currentBounds.fromFrame / fps + compositeSegmentDragStart;
  const compositeEndOffsetTime = compositeSegment.currentBounds.toFrame / fps + compositeSegmentDragEnd;

  const sortedSpeakerSlots = Object.keys(speaker.speakingSlots).sort((slotA, slotB) => +slotA - +slotB);

  // Create deadSpace array
  const deadSpaceArray = useMemo(() => {
    let deadSpaces: Array<{ start: number; end: number }> = [];
    const speakerWords = transcript.filter(
      word => word.speaker_label === speaker.key && word.type !== WordType.Punctuation
    );

    if (speakerWords.length === 0) {
      return deadSpaces;
    }

    const sortedWords = speakerWords.sort((a, b) => a.start_time - b.start_time);

    for (let i = 1; i < sortedWords.length; i++) {
      const currentWord = sortedWords[i];
      const previousWord = sortedWords[i - 1];

      const gapStart = previousWord.end_time || previousWord.start_time;
      const gapEnd = currentWord.start_time;
      const gapDuration = gapEnd - gapStart;

      if (gapDuration > DEAD_SPACE_MIN_DURATION) {
        deadSpaces.push({
          start: gapStart,
          end: gapEnd
        });
      }
    }

    deadSpaces = deadSpaces.filter(deadSpace => {
      return Object.keys(speaker.speakingSlots).some(
        slot => +deadSpace.start >= +slot && +deadSpace.end <= speaker.speakingSlots[slot]
      );
    });

    return deadSpaces;
  }, [transcript, speaker.key, speaker.speakingSlots]);

  const deadSpaceWithAndOffset = useMemo(
    () =>
      deadSpaceArray
        .map(deadSpace => {
          const trimAdjustedStartTime = Math.max(deadSpace.start - compositeStartOffsetTime, 0) * fps;
          const trimAdjustedEndTime =
            Math.min(deadSpace.end - compositeStartOffsetTime, compositeEndOffsetTime - compositeStartOffsetTime) * fps;
          const width = trimAdjustedEndTime - trimAdjustedStartTime;
          return {
            start: trimAdjustedStartTime,
            width
          };
        })
        .filter(deadSpace => deadSpace.width > 0),
    [deadSpaceArray, compositeStartOffsetTime, fps, compositeEndOffsetTime]
  );

  return (
    <div data-testid="timeline-speaker" className={`flex w-full flex-col px-[4px]`}>
      <div
        className={`flex flex-col justify-between overflow-hidden rounded-[3px] py-[2px] ${speaker.lightBgColor}`}
        style={speakerCount === 1 ? { height: '46px' } : {}}
      >
        <div
          className={`h-[18px] w-fit overflow-hidden truncate whitespace-nowrap rounded-sm p-[3px] text-[9px]/[1.2] font-semibold ${speaker.color}`}
        >
          {speaker.first_name} {speaker.last_name}
        </div>
        <div data-testid="timeline-speaker-slots" className="relative mx-[-6px] mb-px mt-[2px] flex h-[5px] w-full">
          {sortedSpeakerSlots.map(startTime => {
            const endTime = speaker.speakingSlots[startTime];
            if (endTime < compositeStartOffsetTime || +startTime > compositeEndOffsetTime) {
              return <Fragment key={startTime}></Fragment>;
            }
            const trimAdjustedStartTime = Math.max(+startTime - compositeStartOffsetTime, 0) * fps;
            const trimAdjustedEndTime =
              Math.min(endTime - compositeStartOffsetTime, compositeEndOffsetTime - compositeStartOffsetTime) * fps;
            const width = trimAdjustedEndTime - trimAdjustedStartTime;
            return (
              <span
                className={`${speaker.bgColor} absolute inline-block h-full rounded-full`}
                style={{
                  width: `calc(${width}px * var(--timeline-zoom))`,
                  left: `calc(${trimAdjustedStartTime}px * var(--timeline-zoom))`
                }}
                key={startTime}
              />
            );
          })}

          {deadSpaceWithAndOffset.map(({ width, start }, index) => {
            return (
              <span
                className={`absolute flex h-1.5 translate-y-[-0.5px] @container`}
                style={{
                  width: `calc(${width}px * var(--timeline-zoom))`,
                  left: `calc(${start}px * var(--timeline-zoom))`
                }}
                key={`deadSpace-${index}`}
              >
                <div className={`hidden size-full ${speaker.lightBgColor} ${DEAD_SPACE_VISIBILITY_CLASS}`}>
                  <span
                    className={`absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 ${speaker.bgColor} size-1 rounded-sm`}
                  />
                  <span
                    className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 ${speaker.bgColor} size-1 rounded-sm`}
                  />
                </div>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
